import { cn } from '@heidi/utils';
import { type ReactNode } from 'react';
import type { StreamdownProps } from 'streamdown';
import { resolveInternalHref } from '../config';

// Derive MarkdownComponents from StreamdownProps to avoid react-markdown type import
export type MarkdownComponents = NonNullable<StreamdownProps['components']>;

// Markdown component props type
export type MdComponentProps = {
  className?: string;
  children?: ReactNode;
  [key: string]: unknown;
};

// Markdown components for chat messages
// Inline markdown components for EVIDENCE mode citation rendering
// Paragraphs render as inline spans so citation badges appear inline
// Uses font-medium for a lighter weight than default bold
// `dir="auto"` on every block-level element: the content is unlocalized
// LLM/backend markdown, so each block resolves its own text direction from
// its first strong character instead of inheriting the UI direction. Without
// it, English content in an RTL locale gets RTL paragraph direction and the
// bidi algorithm mirrors/relocates neutral characters (e.g. `≥126 mg/dL…`
// renders as `mg/dL… 126 ≤`). No-op in LTR locales.
export const inlineMdComponents = {
  h1: ({ children }: MdComponentProps) => (
    <span dir="auto" className="mb-2 mt-3 block text-base font-medium">
      {children}
    </span>
  ),
  h2: ({ children }: MdComponentProps) => (
    <span dir="auto" className="mb-2 mt-3 block text-sm font-medium">
      {children}
    </span>
  ),
  h3: ({ children }: MdComponentProps) => (
    <span dir="auto" className="mb-1 mt-2 block text-sm font-medium">
      {children}
    </span>
  ),
  h4: ({ children }: MdComponentProps) => (
    <span dir="auto" className="mb-1 mt-2 block text-sm font-medium">
      {children}
    </span>
  ),
  h5: ({ children }: MdComponentProps) => (
    <span dir="auto" className="mb-1 mt-2 block text-sm font-medium">
      {children}
    </span>
  ),
  p: ({ children }: MdComponentProps) => {
    // Empty paragraph (from \n\n) → render as line break instead of invisible empty span
    const isEmpty =
      !children ||
      (typeof children === 'string' && !children.trim()) ||
      (Array.isArray(children) && children.length === 0);
    if (isEmpty) {
      return <br />;
    }
    return (
      <span dir="auto" className="text-sm leading-6">
        {children}
      </span>
    );
  },
  // Keep other elements simple for inline context
  strong: ({ children }: MdComponentProps) => (
    <strong className="font-medium">{children}</strong>
  ),
  em: ({ children }: MdComponentProps) => <em>{children}</em>,
  a: ({ children, href }: MdComponentProps) => {
    const openInPlace = resolveInternalHref(href as unknown as string);
    if (openInPlace) {
      return (
        <a
          href={href as unknown as string}
          onClick={(e) => {
            e.preventDefault();
            openInPlace();
          }}
          className="cursor-pointer break-words text-text-link underline"
        >
          {children}
        </a>
      );
    }
    return (
      <a
        href={href as unknown as string}
        target="_blank"
        rel="noopener noreferrer"
        className="break-words text-text-link underline"
      >
        {children}
      </a>
    );
  },
  ul: ({ children }: MdComponentProps) => (
    <span dir="auto" className="my-2 block text-sm">
      {children}
    </span>
  ),
  ol: ({ children }: MdComponentProps) => (
    <span dir="auto" className="my-2 block text-sm">
      {children}
    </span>
  ),
  li: ({ children }: MdComponentProps) => {
    // Filter empty list items to avoid orphan bullets
    const isEmpty =
      !children ||
      (typeof children === 'string' && !children.trim()) ||
      (Array.isArray(children) && children.length === 0);
    if (isEmpty) {
      return null;
    }
    return (
      <span dir="auto" className="ms-4 block text-sm before:me-2 before:content-['•']">
        {children}
      </span>
    );
  },
  // Code is fixed `dir="ltr"` (not auto): source syntax reads left-to-right
  // even when it contains RTL string literals, and neutral-heavy snippets
  // (`>=`, `--flag`) would be bidi-mangled if they inherited an RTL locale.
  code: ({ children }: MdComponentProps) => (
    <code
      dir="ltr"
      className="rounded bg-background-secondary px-1 py-0.5 font-mono text-xs"
    >
      {children}
    </code>
  ),
};

/**
 * `mdComponents` minus its `a:` renderer — the citation badge, which stays
 * app-side with the favicon lookup and the analytics both its click paths fire.
 * Re-attached by the app at module scope so the map a `Streamdown` sees stays
 * referentially stable.
 */
export const legacyMarkdownBase = {
  h1: ({ className, children, ...props }: MdComponentProps) => (
    <h1 dir="auto" className={cn('mb-2 mt-4 text-lg font-bold', className)} {...props}>
      {children}
    </h1>
  ),
  h2: ({ className, children, ...props }: MdComponentProps) => (
    <h2 dir="auto" className={cn('mb-2 mt-3 text-base font-bold', className)} {...props}>
      {children}
    </h2>
  ),
  h3: ({ className, children, ...props }: MdComponentProps) => (
    <h3 dir="auto" className={cn('mb-1 mt-3 text-sm font-bold', className)} {...props}>
      {children}
    </h3>
  ),
  p: ({ className, children, ...props }: MdComponentProps) => (
    <p dir="auto" className={cn('text-sm leading-6', className)} {...props}>
      {children}
    </p>
  ),
  ul: ({ className, children, ...props }: MdComponentProps) => (
    <ul
      dir="auto"
      className={cn('mb-3 list-disc break-words ps-6 text-sm', className)}
      {...props}
    >
      {children}
    </ul>
  ),
  ol: ({ className, children, ...props }: MdComponentProps) => (
    <ol dir="auto" className={cn('mb-3 list-decimal ps-6 text-sm', className)} {...props}>
      {children}
    </ol>
  ),
  li: ({ className, children, ...props }: MdComponentProps) => (
    <li dir="auto" className={cn('mb-1 break-words text-sm', className)} {...props}>
      {children}
    </li>
  ),
  blockquote: ({ className, children, ...props }: MdComponentProps) => (
    <blockquote
      dir="auto"
      className={cn('my-3 border-s-4 border-outline ps-4 text-sm italic', className)}
      {...props}
    >
      {children}
    </blockquote>
  ),
  // Code is fixed `dir="ltr"` (not auto) — see the inlineMdComponents.code note.
  code: ({ className, children, ...props }: MdComponentProps) => (
    <code
      dir="ltr"
      className={cn(
        'rounded bg-background-secondary px-1 py-0.5 font-mono text-xs',
        className
      )}
      {...props}
    >
      {children}
    </code>
  ),
  pre: ({ className, children, ...props }: MdComponentProps) => (
    <pre
      dir="ltr"
      className={cn(
        'my-3 overflow-x-auto rounded-lg bg-background-secondary p-3 font-mono text-xs',
        className
      )}
      {...props}
    >
      {children}
    </pre>
  ),
  hr: ({ className, ...props }: MdComponentProps) => (
    <hr className={cn('my-4 border-outline', className)} {...props} />
  ),
  table: ({ className, children, ...props }: MdComponentProps) => (
    <div className="my-3 grid grid-cols-1 overflow-hidden rounded-md border shadow-sm">
      <div className="min-w-0 overflow-x-auto">
        <table className={cn('min-w-full text-xs', className)} {...props}>
          {children}
        </table>
      </div>
    </div>
  ),
  thead: ({ className, children, ...props }: MdComponentProps) => (
    <thead className={cn('bg-surface-hover', className)} {...props}>
      {children}
    </thead>
  ),
  th: ({ className, children, ...props }: MdComponentProps) => (
    <th
      dir="auto"
      className={cn(
        'whitespace-nowrap border-b border-r px-3 py-2 text-start text-xs font-medium text-text-secondary last:border-r-0',
        className
      )}
      {...props}
    >
      {children}
    </th>
  ),
  td: ({ className, children, ...props }: MdComponentProps) => (
    <td
      dir="auto"
      className={cn(
        'whitespace-nowrap border-b border-r bg-surface-hover/20 px-3 py-2 text-xs font-normal last:border-r-0 [tr:last-child_&]:border-b-0',
        className
      )}
      {...props}
    >
      {children}
    </td>
  ),
};
